import {
    assignModelOptions,
    CompositeModel,
    ConfigConstants,
    CustomInjector,
    ModelOptionsLoaderService,
    NumberModel,
    ODataObject,
    TextModel,
} from '@evorait/pace-core';

export class PISparePartModel extends CompositeModel {
    public description: TextModel;
    public material: TextModel;
    public pIDocNo: TextModel;
    public isEntryDeletable = false;
    public quantity: NumberModel;
    public isBatchNumberRequired: boolean;
    public batchNumber: TextModel;
    public comment: TextModel;

    public constructor(data?: OdataPISparePartModel) {
        super('PIMaterialModel', {});
        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);

        this.description = <TextModel>this.set(new TextModel('Description', data?.Description));
        modelOptionsLoader.load(this.description, 'PISpareParts', 'Description');
        this.material = <TextModel>this.set(new TextModel('Material', data?.Material));
        modelOptionsLoader.load(this.material, 'PISpareParts', 'Material');
        this.pIDocNo = <TextModel>this.set(new TextModel('PIDocNo', data?.PIDocNo));
        modelOptionsLoader.load(this.pIDocNo, 'PISpareParts', 'PIDocNo');
        this.quantity = <NumberModel>this.set(new NumberModel('Quantity', undefined));
        modelOptionsLoader.load(this.quantity, 'PISpareParts', 'Quantity');
        this.isBatchNumberRequired = data?.IsBatchNumberRequired === ConfigConstants.sapTrue;
        this.batchNumber = <TextModel>this.set(new TextModel('BatchNumber', ''));
        modelOptionsLoader.load(this.batchNumber, 'PISpareParts', 'BatchNumber');
        this.comment = <TextModel>this.set(new TextModel('Comment', ''));
        modelOptionsLoader.load(this.comment, 'PISpareParts', 'Comment');
        this.isEntryDeletable = !data;

        if (this.isEntryDeletable) {
            this.quantity = assignModelOptions(
                this.quantity,
                {
                    ...this.quantity.options,
                    minValue: 1,
                },
                this,
            );
            this.clearDocumentNumber();
        }
    }

    // Split batch rows are deletable too, but they belong to a backend material and keep its document number.
    public get isAddedByUser(): boolean {
        return this.isEntryDeletable && !this.isBatchNumberRequired;
    }

    // A material added by the user has no PI document yet, so the backend expects a blank document number.
    private clearDocumentNumber(): void {
        this.pIDocNo.value = '';
        this.pIDocNo.options = {
            ...this.pIDocNo.options,
            required: false,
            readonly: true,
        };
    }

    // If quantity is 0, then batch number is not mandatory, but it is mandatory to add comment
    // If quantity is more than 1 or not yet provided, then batch number is mandatory, but comment is not
    public validateFields(): void {
        this.comment = assignModelOptions(
            this.comment,
            {
                ...this.comment.options,
                required: this.quantity.value === 0,
            },
            this,
        );

        if (this.isBatchNumberRequired) {
            this.batchNumber = assignModelOptions(
                this.batchNumber,
                {
                    ...this.batchNumber.options,
                    required: this.quantity.value !== 0,
                },
                this,
            );
        }
    }

    public getOutModel(): ODataPISparePartOutModel {
        const outModel = <ODataPISparePartOutModel>super.getOutModel();
        // modify the datatype of 'quantity' as gateway expects a string, not a number
        outModel.Quantity = outModel.Quantity ? outModel.Quantity.toString() : '0';
        return outModel;
    }

    public toJSON(): JsonPISparePartModel {
        return {
            description: this.description.value,
            material: this.material.value,
            pIDocNo: this.pIDocNo.value,
            isEntryDeletable: this.isEntryDeletable,
            quantity: this.quantity.value ? this.quantity.value : 0,
            isBatchNumberRequired: this.isBatchNumberRequired,
            batchNumber: this.batchNumber.value,
            comment: this.comment.value,
        };
    }
}

export class PISparePartModelFromJson extends PISparePartModel {
    public constructor(data: JsonPISparePartModel) {
        super();
        this.description.value = data.description;
        this.material.value = data.material;
        this.quantity.value = data.quantity;
        this.isEntryDeletable = data.isEntryDeletable;
        this.isBatchNumberRequired = data.isBatchNumberRequired;
        this.comment.value = data.comment;
        // Drafts saved before the document number was locked may still hold a typed value for an added material.
        this.pIDocNo.value = this.isAddedByUser ? '' : data.pIDocNo;

        if (!this.isEntryDeletable) {
            this.quantity.options = {
                ...this.quantity.options,
                minValue: 0,
            };
        }
        if (this.isBatchNumberRequired) {
            this.batchNumber.value = data.batchNumber;
        }
        this.validateFields();
    }
}

export interface JsonPISparePartModel {
    description: string;
    material: string;
    pIDocNo: string;
    isEntryDeletable: boolean;
    quantity: number;
    isBatchNumberRequired: boolean;
    batchNumber: string;
    comment: string;
}

export interface OdataPISparePartModel {
    Material: string;
    PIDocNo: string;
    Description: string;
    IsBatchNumberRequired: string;
}

export interface ODataPISparePartOutModel extends ODataObject {
    Description: string;
    Material: string;
    PIDocNo: string;
    Quantity: string;
    BatchNumber: string;
    Comment: string;
}
